import axios from 'axios';
import { getPartnerToken } from '@/services/auth/partnerSessionStorage';
import {
  getPartnerSessionCache,
  savePartnerSessionCache,
} from '@/services/offline/partnerSessionCache';
import {
  isBackendUnavailableError,
  markBackendAvailable,
  markBackendUnavailable,
} from '@/services/connectivity/connectivityService';

const DEFAULT_CACHE_MAX_AGE_MS = 15000;
const DEFAULT_TIMEOUT_MS = 5000;

let memoryProfile = null;
let memoryToken = null;
let profileRequest = null;
let profileRequestToken = null;

function normalizedToken(token) {
  const value = token || getPartnerToken();
  return value && !['null', 'undefined'].includes(String(value)) ? value : null;
}

export function isPartnerProfileFresh(
  profile,
  maxAgeMs = DEFAULT_CACHE_MAX_AGE_MS,
) {
  const cachedAt = Date.parse(profile?.cached_at || '');
  return Number.isFinite(cachedAt) && Date.now() - cachedAt < maxAgeMs;
}

export function getCachedPartnerProfile() {
  return memoryToken === getPartnerToken() ? memoryProfile : getPartnerSessionCache();
}

export async function getPartnerProfile(options = {}) {
  const {
    token: requestedToken,
    force = false,
    allowStaleFallback = true,
    maxAgeMs = DEFAULT_CACHE_MAX_AGE_MS,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  } = options;
  const token = normalizedToken(requestedToken);
  if (!token) throw new Error('PARTNER_SESSION_REQUIRED');

  const cached = memoryToken === token && memoryProfile
    ? memoryProfile
    : getPartnerSessionCache(token);
  if (!force && isPartnerProfileFresh(cached, maxAgeMs)) return cached;

  if (profileRequest && profileRequestToken === token) return profileRequest;

  profileRequestToken = token;
  profileRequest = axios.get(
    `${process.env.VUE_APP_API_URL}/v1/api/partner/me`,
    {
      headers: { Authorization: `Bearer ${token}` },
      timeout: timeoutMs,
    },
  ).then((response) => {
    if (token !== getPartnerToken()) throw new Error('PARTNER_SESSION_CHANGED');
    markBackendAvailable();
    const persisted = savePartnerSessionCache(response.data);
    memoryToken = token;
    memoryProfile = {
      ...response.data,
      cached_at: persisted?.cached_at || new Date().toISOString(),
    };
    return memoryProfile;
  }).catch((error) => {
    if (isBackendUnavailableError(error)) {
      markBackendUnavailable(
        error?.message || 'No se pudo actualizar la sesión Partner.',
      );
      if (allowStaleFallback && cached) return cached;
    }
    throw error;
  }).finally(() => {
    if (profileRequestToken === token) {
      profileRequest = null;
      profileRequestToken = null;
    }
  });

  return profileRequest;
}

export function clearPartnerProfileMemory() {
  memoryProfile = null;
  memoryToken = null;
  profileRequest = null;
  profileRequestToken = null;
}
